import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUploadField } from "@/components/ui/image-upload-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PlusCircle, Edit, Trash2, RefreshCw, Coins, Gem, Layers, Users, Grid, Star, TrendingUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";

const achievementCategories = [
  {
    value: "gameplay",
    label: "Gameplay",
    icon: <Layers className="w-4 h-4 mr-2" />,
  },
  {
    value: "social",
    label: "Social",
    icon: <Users className="w-4 h-4 mr-2" />,
  },
  {
    value: "collection",
    label: "Collection",
    icon: <Grid className="w-4 h-4 mr-2" />,
  },
  {
    value: "special",
    label: "Special Events",
    icon: <Star className="w-4 h-4 mr-2" />,
  },
  {
    value: "progress",
    label: "Progression",
    icon: <TrendingUp className="w-4 h-4 mr-2" />,
  },
];

interface Achievement {
  id: number;
  name: string;
  description: string;
  icon?: string;
  imageUrl?: string;
  category: string;
  requirement: number;
  rewardAmount: number;
  rewardType: "regular" | "premium";
  isActive: boolean;
}

export function AchievementsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [achievementId, setAchievementId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "gameplay",
    requirement: 1,
    imageUrl: "",
    icon: "",
    rewardAmount: 50,
    rewardType: "regular" as "regular" | "premium",
    isActive: true,
  });

  const { data: achievements = [], isLoading, isError } = useQuery<Achievement[]>({
    queryKey: ["achievements"],
    queryFn: async () => {
      const response = await apiRequest<Achievement[]>("/api/manager/achievements");
      return response;
    }
  });

  const addMutation = useMutation({
    mutationFn: async (data: Omit<Achievement, "id">) => {
      const response = await apiRequest("/api/manager/achievements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achievements"] });
      toast({
        title: "Achievement added",
        description: "The achievement has been added successfully."
      });
      setIsOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding achievement",
        description: error.message || "Failed to add the achievement",
        variant: "destructive"
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Omit<Achievement, "id"> }) => {
      const response = await apiRequest(`/api/manager/achievements/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achievements"] });
      toast({
        title: "Achievement updated",
        description: "The achievement has been updated successfully."
      });
      setIsOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating achievement",
        description: error.message || "Failed to update the achievement",
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(`/api/manager/achievements/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["achievements"] });
      toast({
        title: "Achievement deleted",
        description: "The achievement has been removed successfully."
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting achievement",
        description: error.message || "Failed to delete the achievement",
        variant: "destructive"
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "gameplay",
      requirement: 1,
      imageUrl: "",
      icon: "",
      rewardAmount: 50,
      rewardType: "regular",
      isActive: true,
    });
    setAchievementId(null);
  };

  const handleAdd = () => {
    setMode("add");
    resetForm();
    setIsOpen(true);
  };

  const handleEdit = async (achievement: Achievement) => {
    console.log('Editing achievement:', achievement);
    setMode("edit");
    setAchievementId(achievement.id);
    
    try {
      // Get fresh achievement data
      const updatedAchievement = await apiRequest<Achievement>(`/api/manager/achievements/${achievement.id}`);
      
      // Ensure category matches one of the valid category values
      const validCategory = achievementCategories.find(c => c.value === updatedAchievement.category)?.value || "gameplay";
      
      // Ensure reward type is exactly "regular" or "premium"
      const validRewardType = updatedAchievement.rewardType === "premium" ? "premium" : "regular";
      
      setFormData({
        name: updatedAchievement.name,
        description: updatedAchievement.description,
        category: validCategory,
        requirement: updatedAchievement.requirement,
        imageUrl: updatedAchievement.icon || updatedAchievement.imageUrl || "",
        icon: updatedAchievement.icon || updatedAchievement.imageUrl || "",
        rewardAmount: updatedAchievement.rewardAmount,
        rewardType: validRewardType,
        isActive: updatedAchievement.isActive,
      });
      setIsOpen(true);
    } catch (error) {
      console.error('Error fetching achievement:', error);
      toast({
        title: "Error",
        description: "Failed to load achievement details",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submissionData = {
      ...formData,
      icon: formData.imageUrl || formData.icon,
    };
    
    if (mode === "add") {
      addMutation.mutate(submissionData);
    } else if (achievementId) {
      updateMutation.mutate({ id: achievementId, data: submissionData });
    }
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading achievements</h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["achievements"] })}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Button onClick={handleAdd}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Achievement
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) resetForm();
        setIsOpen(open);
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {mode === "add" ? "Add New Achievement" : "Edit Achievement"}
            </DialogTitle>
            <DialogDescription>
              {mode === "add"
                ? "Enter the details for the new achievement."
                : "Update the details for this achievement."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Achievement Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter achievement name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, category: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {achievementCategories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          <div className="flex items-center">
                            {category.icon}
                            {category.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter achievement description"
                  required
                />
              </div>

              <div className="space-y-2">
                <ImageUploadField
                  id="imageUrl"
                  label="Image URL (Optional)"
                  value={formData.imageUrl || ""}
                  onChange={(value) =>
                    setFormData({ ...formData, imageUrl: value })
                  }
                  placeholder="Enter image URL or upload an image"
                  uploadEndpoint="/api/manager/upload-image"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="requirement">Requirement</Label>
                  <Input
                    id="requirement"
                    type="number"
                    min="1"
                    step="1"
                    value={formData.requirement}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requirement: parseInt(e.target.value) || 1,
                      })
                    }
                    placeholder="Enter requirement value"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rewardAmount">Reward Amount</Label>
                  <Input
                    id="rewardAmount"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.rewardAmount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        rewardAmount: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="Enter reward amount"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rewardType">Reward Type</Label>
                <Select
                  value={formData.rewardType}
                  onValueChange={(value: "regular" | "premium") =>
                    setFormData({ ...formData, rewardType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reward type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">
                      <div className="flex items-center">
                        <Coins className="w-4 h-4 mr-2 text-yellow-500" />
                        Regular (Coins)
                      </div>
                    </SelectItem>
                    <SelectItem value="premium">
                      <div className="flex items-center">
                        <Gem className="w-4 h-4 mr-2 text-purple-500" />
                        Premium (Gems)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addMutation.isPending || updateMutation.isPending} className="ml-2">
                {(addMutation.isPending || updateMutation.isPending) ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {mode === "add" ? "Adding..." : "Updating..."}
                  </>
                ) : (
                  <>{mode === "add" ? "Add Achievement" : "Update Achievement"}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Table>
        <TableCaption>A list of all achievements</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Icon</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Reward</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {achievements.map((achievement) => (
            <TableRow key={achievement.id}>
              <TableCell>
                <div className="w-8 h-8 rounded-lg bg-black/40 flex items-center justify-center overflow-hidden">
                  <img 
                    src={achievement.icon || achievement.imageUrl || '/achievements/default.png'} 
                    alt={achievement.name} 
                    className="w-full h-full object-contain"
                  />
                </div>
              </TableCell>
              <TableCell>{achievement.name}</TableCell>
              <TableCell className="max-w-[200px] truncate">{achievement.description}</TableCell>
              <TableCell>
                <span className="capitalize">{achievement.category}</span>
              </TableCell>
              <TableCell>{achievement.rewardAmount} {achievement.rewardType === 'premium' ? 'Gems' : 'Coins'}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  achievement.isActive 
                    ? "bg-green-100 text-green-800" 
                    : "bg-red-100 text-red-800"
                }`}>
                  {achievement.isActive ? "Active" : "Inactive"}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(achievement)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Achievement</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this achievement? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(achievement.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}